import { useMemo } from 'react'
import { XIcon } from 'lucide-react'
import { useGetUsersQuery } from '@axilon-apps/domains-identity/state'
import type { NotificationRecipients } from '@axilon-apps/domains-ontology-ui-state'

import { Checkbox } from '../ui/checkbox'
import { NativeSelect, NativeSelectOption } from '../ui/native-select'
import { normalizeRecipients, RECIPIENT_ROLES } from './recipients'

export interface RecipientsFieldProps {
  value: NotificationRecipients | undefined
  onChange: (next: NotificationRecipients) => void
  disabled?: boolean
  /** What the default audience is called when nothing is picked. */
  defaultLabel: string
}

/**
 * Who a rule or agent addresses in the bell and by email: roles (which also
 * reach everyone above them) and specific people. Empty means the default.
 */
export function RecipientsField({ value, onChange, disabled, defaultLabel }: RecipientsFieldProps) {
  const recipients = normalizeRecipients(value)
  const users = useGetUsersQuery({ page: 1, perPage: 100, enabled: true })
  const byId = useMemo(() => new Map((users.data?.items ?? []).map((u) => [u.id, u])), [users.data])
  const addable = (users.data?.items ?? []).filter((u) => !recipients.user_ids.includes(u.id))

  const setRole = (role: string, on: boolean) =>
    onChange({
      ...recipients,
      roles: on
        ? [...recipients.roles.filter((r) => r !== role), role]
        : recipients.roles.filter((r) => r !== role)
    })

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        {RECIPIENT_ROLES.map((role) => (
          <label key={role} className="flex items-center gap-1.5 text-13 capitalize text-ink">
            <Checkbox
              checked={recipients.roles.includes(role)}
              disabled={disabled}
              onCheckedChange={(checked) => setRole(role, checked === true)}
            />
            {role}
          </label>
        ))}
      </div>
      {recipients.user_ids.length > 0 ? (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {recipients.user_ids.map((id) => {
            const user = byId.get(id)
            const label = user ? user.name || user.username : id
            return (
              <li
                key={id}
                className="inline-flex max-w-full items-center gap-1 rounded border border-line bg-bg-elev/60 px-2 py-1 text-xs"
              >
                <span className="truncate">{label}</span>
                <button
                  type="button"
                  aria-label={`Remove ${label}`}
                  disabled={disabled}
                  className="shrink-0 text-ink-4 hover:text-ink"
                  onClick={() =>
                    onChange({
                      ...recipients,
                      user_ids: recipients.user_ids.filter((u) => u !== id)
                    })
                  }
                >
                  <XIcon className="size-3.5" />
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
      <NativeSelect
        aria-label="Add a person"
        value=""
        disabled={disabled || addable.length === 0}
        onChange={(e) => {
          const id = e.target.value
          if (id) onChange({ ...recipients, user_ids: [...recipients.user_ids, id] })
        }}
      >
        <NativeSelectOption value="">Add a person…</NativeSelectOption>
        {addable.map((u) => (
          <NativeSelectOption key={u.id} value={u.id}>
            {u.name || u.username}
            {u.email ? ` (${u.email})` : ''}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <p className="m-0 text-xs text-ink-4">
        {recipients.roles.length === 0 && recipients.user_ids.length === 0
          ? `Nobody picked: goes to ${defaultLabel}.`
          : 'They see it in the bell and get it by email if they keep email on.'}
      </p>
    </div>
  )
}
